import React from "react";
import { ArrowRight, ShieldCheck, ChartLineUp, Buildings, ChatCircleDots } from "@phosphor-icons/react";

const HIGHLIGHTS = [
  { icon: ShieldCheck, text: "Proactive 24/7 monitoring" },
  { icon: ChartLineUp, text: "Predictable per-user pricing" },
  { icon: Buildings, text: "Built for 5–500+ user teams" },
  { icon: ChatCircleDots, text: "Same-day response on critical tickets" },
];

export default function BusinessHero({ onBook }) {
  return (
    <section
      id="business-it"
      data-testid="business-it-hero"
      className="relative bg-zinc-950 text-white py-20 md:py-28 overflow-hidden"
    >
      {/* Decorative grid + glow */}
      <div className="absolute inset-0 hero-grid opacity-50" />
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full"
        style={{
          background: "radial-gradient(closest-side, rgba(37,99,235,0.35), transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs tracking-widest uppercase text-blue-300">
            <Buildings size={14} weight="fill" /> For Businesses
          </div>
          <h2 className="mt-5 font-heading font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Technology problems
            <span className="block text-blue-500">cost money.</span>
          </h2>
          <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
            TechConnect LLC keeps your business running with proactive monitoring, cybersecurity,
            backups, Microsoft 365 support, network management, and fast remote assistance — all on
            a predictable monthly plan.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => onBook && onBook("MSP — Business IT Assessment")}
              data-testid="business-it-assessment-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 font-semibold shadow-lg shadow-blue-600/30 transition-all"
            >
              Get a Business IT Assessment
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => onBook && onBook("Business IT Support")}
              data-testid="business-it-consult-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white px-6 py-3.5 font-semibold transition-all"
            >
              Schedule a Consultation
            </button>
          </div>
        </div>

        {/* Right side: 2x2 grid of highlights */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, text }, i) => (
            <div
              key={i}
              data-testid={`business-highlight-${i}`}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm hover:bg-white/[0.07] transition-colors"
            >
              <div className="h-10 w-10 rounded-lg bg-blue-600/20 text-blue-300 grid place-items-center">
                <Icon size={20} weight="duotone" />
              </div>
              <div className="mt-3 text-sm font-semibold text-white leading-snug">{text}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
